export type KnowledgeScopeLibraryOption = {
  id: string;
  name: string;
};

export type KnowledgeScopeWorkspaceOption = {
  id: string;
  name: string;
  libraries: KnowledgeScopeLibraryOption[];
};

export type KnowledgeScopeOptions = {
  workspaces: KnowledgeScopeWorkspaceOption[];
  publicLibraries: KnowledgeScopeLibraryOption[];
};

/** Applied Knowledge Search scope sent to supervisor-chat (matches Mastra KnowledgeSearchScope). */
export type KnowledgeSearchScopePayload = {
  workspaceId?: string;
  libraryIds?: string[];
  documentIds?: string[];
  includePublic?: boolean;
  publicLibraryIds?: string[];
};

/** Draft selection in the Manage Scope dialog. */
export type KnowledgeScopeSelection = {
  libraryIds: string[];
  publicLibraryIds: string[];
};

export const EMPTY_KNOWLEDGE_SCOPE_SELECTION: KnowledgeScopeSelection = {
  libraryIds: [],
  publicLibraryIds: [],
};

/** Select every private + public library from loaded scope options. */
export function selectAllKnowledgeScope(options: KnowledgeScopeOptions): KnowledgeScopeSelection {
  const libraryIds: string[] = [];
  for (const workspace of options.workspaces) {
    for (const library of workspace.libraries) {
      libraryIds.push(library.id);
    }
  }
  return {
    libraryIds,
    publicLibraryIds: options.publicLibraries.map((library) => library.id),
  };
}

export function countSelectedLibraries(selection: KnowledgeScopeSelection): number {
  return selection.libraryIds.length + selection.publicLibraryIds.length;
}

export function knowledgeScopeSelectionLabel(selection: KnowledgeScopeSelection | null): string {
  if (!selection || countSelectedLibraries(selection) === 0) return "All accessible";
  const n = countSelectedLibraries(selection);
  return `${n} ${n === 1 ? "library" : "libraries"} in scope`;
}

/** Resolve selected library IDs to `{ id, name }` using scope options (private + public). */
export function resolveSelectedLibraries(
  selection: KnowledgeScopeSelection | null,
  options: KnowledgeScopeOptions | null,
): KnowledgeScopeLibraryOption[] {
  if (!selection || !options || countSelectedLibraries(selection) === 0) return [];

  const byId = new Map<string, string>();
  for (const workspace of options.workspaces) {
    for (const library of workspace.libraries) {
      byId.set(library.id, library.name);
    }
  }
  for (const library of options.publicLibraries) {
    byId.set(library.id, library.name);
  }

  const result: KnowledgeScopeLibraryOption[] = [];
  for (const id of selection.libraryIds) {
    const name = byId.get(id);
    if (name) result.push({ id, name });
  }
  for (const id of selection.publicLibraryIds) {
    const name = byId.get(id);
    if (name) result.push({ id, name });
  }
  return result;
}

/**
 * Collapsed header summary for the composer scope bar.
 * 0 → "All accessible"; 1 → library name; many → "N libraries".
 */
export function knowledgeScopeHeaderSummary(
  selectedLibraries: KnowledgeScopeLibraryOption[],
  selection: KnowledgeScopeSelection | null = null,
): string {
  if (selectedLibraries.length === 1) return selectedLibraries[0].name;
  if (selectedLibraries.length > 1) return `${selectedLibraries.length} libraries`;
  // Options not loaded yet but selection has IDs — fall back to count label.
  if (selection && countSelectedLibraries(selection) > 0) {
    return knowledgeScopeSelectionLabel(selection);
  }
  return "All accessible";
}

/** Toggle a private library. Selections may span multiple workspaces. */
export function togglePrivateLibrary(
  selection: KnowledgeScopeSelection,
  _workspaceId: string,
  libraryId: string,
): KnowledgeScopeSelection {
  const alreadySelected = selection.libraryIds.includes(libraryId);
  return {
    ...selection,
    libraryIds: alreadySelected
      ? selection.libraryIds.filter((id) => id !== libraryId)
      : [...selection.libraryIds, libraryId],
  };
}

export function togglePublicLibrary(
  selection: KnowledgeScopeSelection,
  libraryId: string,
): KnowledgeScopeSelection {
  const selected = selection.publicLibraryIds.includes(libraryId);
  return {
    ...selection,
    publicLibraryIds: selected
      ? selection.publicLibraryIds.filter((id) => id !== libraryId)
      : [...selection.publicLibraryIds, libraryId],
  };
}

/** Count how many of a workspace's libraries are currently selected. */
export function workspaceSelectedCount(
  selection: KnowledgeScopeSelection,
  workspace: KnowledgeScopeWorkspaceOption,
): number {
  if (workspace.libraries.length === 0) return 0;
  const selected = new Set(selection.libraryIds);
  let count = 0;
  for (const library of workspace.libraries) {
    if (selected.has(library.id)) count += 1;
  }
  return count;
}

/** True when every library in the workspace is selected (empty workspace = false). */
export function isWorkspaceFullySelected(
  selection: KnowledgeScopeSelection,
  workspace: KnowledgeScopeWorkspaceOption,
): boolean {
  if (workspace.libraries.length === 0) return false;
  return workspaceSelectedCount(selection, workspace) === workspace.libraries.length;
}

/** True when every public library is selected (empty list = false). */
export function isPublicLibrariesFullySelected(
  selection: KnowledgeScopeSelection,
  publicLibraries: KnowledgeScopeLibraryOption[],
): boolean {
  if (publicLibraries.length === 0) return false;
  const selected = new Set(selection.publicLibraryIds);
  return publicLibraries.every((library) => selected.has(library.id));
}

/**
 * Toggle all libraries in a workspace: if fully selected, clear them; otherwise select all.
 */
export function toggleWorkspace(
  selection: KnowledgeScopeSelection,
  workspace: KnowledgeScopeWorkspaceOption,
): KnowledgeScopeSelection {
  if (workspace.libraries.length === 0) return selection;
  const ids = workspace.libraries.map((library) => library.id);
  if (isWorkspaceFullySelected(selection, workspace)) {
    const remove = new Set(ids);
    return {
      ...selection,
      libraryIds: selection.libraryIds.filter((id) => !remove.has(id)),
    };
  }
  const next = new Set(selection.libraryIds);
  for (const id of ids) next.add(id);
  return {
    ...selection,
    libraryIds: [...next],
  };
}

/**
 * Toggle all public libraries: if fully selected, clear them; otherwise select all.
 */
export function toggleAllPublicLibraries(
  selection: KnowledgeScopeSelection,
  publicLibraries: KnowledgeScopeLibraryOption[],
): KnowledgeScopeSelection {
  if (publicLibraries.length === 0) return selection;
  if (isPublicLibrariesFullySelected(selection, publicLibraries)) {
    const remove = new Set(publicLibraries.map((library) => library.id));
    return {
      ...selection,
      publicLibraryIds: selection.publicLibraryIds.filter((id) => !remove.has(id)),
    };
  }
  const next = new Set(selection.publicLibraryIds);
  for (const library of publicLibraries) next.add(library.id);
  return {
    ...selection,
    publicLibraryIds: [...next],
  };
}

/**
 * Status text for the Knowledge search row in the + menu.
 * Empty → "No item selected"; else "N libraries".
 */
export function knowledgeSearchMenuStatusLabel(
  selection: KnowledgeScopeSelection | null,
): string {
  if (!selection || countSelectedLibraries(selection) === 0) return "No item selected";
  const n = countSelectedLibraries(selection);
  return `${n} ${n === 1 ? "library" : "libraries"}`;
}

/**
 * Build the scope payload for supervisor-chat / retrieve.
 * Uses libraryIds without workspaceId (RAG resolveRequestedScope).
 */
export function toKnowledgeSearchScopePayload(
  selection: KnowledgeScopeSelection | null,
  options?: { documentIds?: string[] },
): KnowledgeSearchScopePayload | null {
  const documentIds = options?.documentIds?.filter((id) => id.trim().length > 0) ?? [];
  if (!selection && documentIds.length === 0) return null;
  if (
    selection &&
    selection.libraryIds.length === 0 &&
    selection.publicLibraryIds.length === 0 &&
    documentIds.length === 0
  ) {
    return null;
  }

  const payload: KnowledgeSearchScopePayload = {};
  if (selection && selection.libraryIds.length > 0) {
    payload.libraryIds = [...selection.libraryIds];
  }
  if (selection && selection.publicLibraryIds.length > 0) {
    payload.includePublic = true;
    payload.publicLibraryIds = [...selection.publicLibraryIds];
  }
  if (documentIds.length > 0) {
    payload.documentIds = [...documentIds];
  }
  return Object.keys(payload).length > 0 ? payload : null;
}
